import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format, addDays } from 'date-fns';

import { documentInvoiceService } from '../../../services/documentInvoiceService';
import { CustomerService } from '../../../services/customerService';
import { productService } from '../../../services/productService';
import { customerAddressService, CustomerAddress } from '../../../services/customerAddressService';
import { formatCurrency } from '../../../utils/formatters';

interface InvoiceItem {
  variant_id: string;
  quantity: number | '';
  unit_price: number | '';
  discount_percent: number | '';
  description: string;
  hsn_code: string;
  tax_rate?: number;
}

interface InvoiceFormData {
  customer_id: string;
  invoice_date: string;
  due_date: string;
  billing_address_id: string;
  shipping_address_id: string;
  discount_amount: number | '';
  shipping_charges: number | '';
  packing_charges: number | '';
  other_charges: any[];
  notes: string;
  terms_and_conditions: string;
  items: InvoiceItem[];
}

const InvoiceForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState<InvoiceFormData>({
    customer_id: '',
    invoice_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'), // 30 days due date default
    billing_address_id: '',
    shipping_address_id: '',
    discount_amount: 0,
    shipping_charges: 0,
    packing_charges: 0,
    other_charges: [],
    notes: '',
    terms_and_conditions: '',
    items: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [invoice, setInvoice] = useState<any>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Items, customer, addresses, and charges change what was legally invoiced - once the
  // invoice has left DRAFT (sent/partially paid/overdue) the backend rejects changes to
  // those fields, so lock them here too rather than letting the user edit something that
  // will silently be dropped on save.
  const financialsLocked = isEditing && !!invoice && invoice.status !== 'draft';
  const [customersData, setCustomersData] = useState<any>(null);
  const [productsData, setProductsData] = useState<any>(null);
  const [stockInfo, setStockInfo] = useState<Record<string, any>>({});

  // Warehouse state - persisted in localStorage
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>(() => {
    return localStorage.getItem('admin_selectedWarehouseId') || '';
  });

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch customers
        const customers = await CustomerService.getCustomers({ page: 1, per_page: 100 });
        setCustomersData(customers);

        // Fetch warehouses
        const token = localStorage.getItem('token');
        const whRes = await fetch('/api/admin/inventory/warehouses', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (whRes.ok) {
          const whData = await whRes.json();
          setWarehouses(whData);
          if (!localStorage.getItem('admin_selectedWarehouseId') && whData.length > 0) {
            setSelectedWarehouseId(whData[0].id);
            localStorage.setItem('admin_selectedWarehouseId', whData[0].id);
          }
        }

        // Fetch products
        const products = await productService.listProducts({ page: 1, per_page: 1000 });
        setProductsData(products);

        // Fetch existing invoice if editing
        if (isEditing && id) {
          setInvoiceLoading(true);
          const invoiceData = await documentInvoiceService.getInvoice(id) as any;
          setInvoice(invoiceData);
          setFormData({
            customer_id: invoiceData.customer_id,
            invoice_date: invoiceData.invoice_date,
            due_date: invoiceData.due_date,
            billing_address_id: invoiceData.billing_address_id,
            shipping_address_id: invoiceData.shipping_address_id,
            discount_amount: invoiceData.discount_amount,
            shipping_charges: invoiceData.shipping_charges,
            packing_charges: invoiceData.packing_charges,
            other_charges: invoiceData.other_charges || [],
            notes: invoiceData.notes || '',
            terms_and_conditions: invoiceData.terms_and_conditions || '',
            items: invoiceData.items.map((item: any) => ({
              variant_id: item.variant_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
              discount_percent: item.discount_percent,
              description: item.description || '',
              hsn_code: item.hsn_code || ''
            }))
          });
          setInvoiceLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      }
    };

    fetchData();
  }, [id, isEditing, enqueueSnackbar]);

  // Load customer addresses when customer is selected
  useEffect(() => {
    const loadCustomerAddresses = async () => {
      if (formData.customer_id && customersData?.items) {
        const customer = customersData.items.find((c: any) => c.id === formData.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
          setAddressesLoading(true);
          
          try {
            const addressResponse = await customerAddressService.getCustomerAddresses(formData.customer_id);
            setCustomerAddresses(addressResponse.addresses);
            
            // Auto-select default addresses if not already set (only for new invoices)
            if (!isEditing) {
              if (!formData.billing_address_id) {
                const defaultBilling = addressResponse.addresses.find(
                  addr => (addr.address_type === 'billing' || addr.address_type === 'both') && addr.is_default
                );
                if (defaultBilling) {
                  setFormData(prev => ({ ...prev, billing_address_id: defaultBilling.id }));
                }
              }
              
              if (!formData.shipping_address_id) {
                const defaultShipping = addressResponse.addresses.find(
                  addr => (addr.address_type === 'shipping' || addr.address_type === 'both') && addr.is_default
                );
                if (defaultShipping) {
                  setFormData(prev => ({ ...prev, shipping_address_id: defaultShipping.id }));
                }
              }
            }
          } catch (error) {
            console.error('Failed to load customer addresses:', error);
            enqueueSnackbar('Failed to load customer addresses', { variant: 'error' });
          } finally {
            setAddressesLoading(false);
          }
        }
      } else {
        setCustomerAddresses([]);
        setSelectedCustomer(null);
      }
    };

    loadCustomerAddresses();
  }, [formData.customer_id, customersData, isEditing, enqueueSnackbar]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate form
      const validationErrors: Record<string, string> = {};
      
      if (!formData.customer_id) {
        validationErrors.customer_id = 'Customer is required';
      }
      
      if (!formData.items.length) {
        validationErrors.items = 'At least one item is required';
      } else {
        // Validate each item
        formData.items.forEach((item, index) => {
          if (!item.variant_id) {
            validationErrors[`item_${index}_variant`] = 'Product is required';
          }
          const qty = Number(item.quantity);
          if (isNaN(qty) || qty <= 0) {
            validationErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
          }
          const price = Number(item.unit_price);
          if (isNaN(price) || price < 0.01) {
            validationErrors[`item_${index}_price`] = 'Unit price must be at least ₹0.01';
          }
        });
      }
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the validation errors', { variant: 'error' });
        return;
      }

      // Ensure all numeric fields in items are valid numbers
      const cleanedItems = formData.items.map(item => {
        const quantity = Number(item.quantity);
        const unitPrice = Number(item.unit_price);
        const discountPercent = Number(item.discount_percent);
        
        return {
          ...item,
          quantity: isNaN(quantity) || quantity <= 0 ? 1 : quantity,
          unit_price: isNaN(unitPrice) || unitPrice < 0 ? 0 : unitPrice,
          discount_percent: isNaN(discountPercent) || discountPercent < 0 ? 0 : (discountPercent > 100 ? 100 : discountPercent)
        };
      });

      const cleanedFormData: Record<string, any> = {
        ...formData,
        billing_address_id: formData.billing_address_id && formData.billing_address_id.trim() !== '' ? formData.billing_address_id : undefined,
        shipping_address_id: formData.shipping_address_id && formData.shipping_address_id.trim() !== '' ? formData.shipping_address_id : undefined,
        discount_amount: Number(formData.discount_amount) || 0,
        shipping_charges: Number(formData.shipping_charges) || 0,
        packing_charges: Number(formData.packing_charges) || 0,
        items: cleanedItems
      };

      // Belt-and-suspenders: the backend rejects item/customer/address/charge changes on
      // a non-DRAFT invoice, and the fields above are already disabled in the UI while
      // locked - but strip them from the payload too so a stale/edited state can never
      // accidentally send something the server will reject anyway.
      if (isEditing && financialsLocked) {
        delete cleanedFormData.items;
        delete cleanedFormData.customer_id;
        delete cleanedFormData.billing_address_id;
        delete cleanedFormData.shipping_address_id;
        delete cleanedFormData.discount_amount;
        delete cleanedFormData.shipping_charges;
        delete cleanedFormData.packing_charges;
        delete cleanedFormData.other_charges;
      }

      if (isEditing) {
        await documentInvoiceService.updateInvoice(id!, cleanedFormData);
        enqueueSnackbar('Invoice updated successfully', { variant: 'success' });
        navigate(`/admin/documents/invoices/${id}`);
      } else {
        console.log('Submitting invoice with cleaned data:', cleanedFormData);
        const result = await documentInvoiceService.createInvoice(cleanedFormData);
        enqueueSnackbar('Invoice created successfully', { variant: 'success' });
        navigate(`/admin/documents/invoices/${result.id}`);
      }
    } catch (error: any) {
      console.error('Failed to save invoice:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save invoice';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch stock info for a variant (warehouse-filtered)
  const fetchStockInfo = async (variantId: string, warehouseId?: string) => {
    try {
      const token = localStorage.getItem('token');
      const wh = warehouseId || selectedWarehouseId;
      const url = wh
        ? `/api/admin/inventory/stock?search=${variantId}&warehouse_id=${wh}`
        : `/api/admin/inventory/stock?search=${variantId}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          const stock = data.items[0];
          setStockInfo(prev => ({
            ...prev,
            [variantId]: {
              current_stock: stock.current_stock,
              reserved_stock: stock.reserved_stock,
              available_stock: stock.available_stock,
              is_low_stock: stock.is_low_stock
            }
          }));
        } else {
          setStockInfo(prev => ({
            ...prev,
            [variantId]: { current_stock: 0, reserved_stock: 0, available_stock: 0, is_low_stock: true }
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch stock info:', error);
    }
  };

  // Refetch stock when warehouse changes
  useEffect(() => {
    if (selectedWarehouseId && formData.items.length > 0) {
      formData.items.forEach(item => {
        if (item.variant_id) fetchStockInfo(item.variant_id, selectedWarehouseId);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouseId]);

  // Handle adding new line item
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          variant_id: '',
          quantity: 1,
          unit_price: 0,
          discount_percent: 0,
          description: '',
          hsn_code: ''
        }
      ]
    }));
  };

  // Handle removing line item
  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Handle line item changes
  const updateLineItem = (index: number, field: keyof InvoiceItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    
    // Clear item-specific errors when user makes changes
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`item_${index}_variant`];
      delete newErrors[`item_${index}_quantity`];
      delete newErrors[`item_${index}_price`];
      return newErrors;
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const itemsSubtotal = formData.items.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      const discountPercent = Number(item.discount_percent) || 0;
      const lineTotal = qty * price;
      const discountAmount = lineTotal * (discountPercent / 100);
      return sum + (lineTotal - discountAmount);
    }, 0);

    const otherChargesTotal = (formData.other_charges || []).reduce((sum, charge) => sum + Number(charge.amount || 0), 0);
    const subtotal = itemsSubtotal + Number(formData.shipping_charges || 0) + Number(formData.packing_charges || 0) + otherChargesTotal - Number(formData.discount_amount || 0);
    const taxAmount = subtotal * 0.18; // 18% GST
    const total = subtotal + taxAmount;

    return { itemsSubtotal, otherChargesTotal, subtotal, taxAmount, total };
  };

  const { itemsSubtotal, otherChargesTotal, subtotal, taxAmount, total } = calculateTotals();

  // Clear errors when form data changes
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const newErrors = { ...errors };
      
      if (formData.customer_id && newErrors.customer_id) {
        delete newErrors.customer_id;
      }
      
      if (formData.items.length > 0 && newErrors.items) {
        delete newErrors.items;
      }
      
      setErrors(newErrors);
    }
  }, [formData.customer_id, formData.items.length, errors]);

  if (invoiceLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/invoices">
          Invoices
        </Link>
        <Typography color="text.primary">
          {isEditing ? 'Edit Invoice' : 'New Invoice'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/invoices')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            {isEditing ? `Edit Invoice ${invoice?.invoice_number}` : 'Create New Invoice'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Invoice'}
        </Button>
      </Box>

      {financialsLocked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This invoice is <strong>{invoice.status}</strong> — items, customer, addresses, and charges
          are locked and can only be changed while an invoice is in DRAFT status. You can still update
          the due date and notes below. To adjust quantities or amounts, issue a sales return / credit
          note against this invoice instead.
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={1.5}>
          {/* Customer & Document Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1.5} alignItems="center">
                  {/* Warehouse Selector Banner */}
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1.5}
                      sx={{
                        bgcolor: selectedWarehouseId ? 'primary.50' : 'warning.50',
                        border: '1px solid',
                        borderColor: selectedWarehouseId ? 'primary.200' : 'warning.300',
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} color={selectedWarehouseId ? 'primary.main' : 'warning.dark'} sx={{ whiteSpace: 'nowrap' }}>
                        🏭 Dispatch Warehouse:
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <Select
                          value={selectedWarehouseId}
                          onChange={(e) => {
                            const wid = e.target.value;
                            setSelectedWarehouseId(wid);
                            localStorage.setItem('admin_selectedWarehouseId', wid);
                            setStockInfo({});
                            formData.items.forEach(item => {
                              if (item.variant_id) fetchStockInfo(item.variant_id, wid);
                            });
                          }}
                          displayEmpty
                          sx={{ bgcolor: 'background.paper' }}
                        >
                          <MenuItem value=""><em>All Warehouses</em></MenuItem>
                          {warehouses.map((wh) => (
                            <MenuItem key={wh.id} value={wh.id}>
                              {wh.name}{wh.city ? ` — ${wh.city}` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {!selectedWarehouseId && (
                        <Typography variant="caption" color="warning.dark">
                          ⚠ Select a warehouse to see accurate per-warehouse stock
                        </Typography>
                      )}
                      {selectedWarehouseId && warehouses.find(w => w.id === selectedWarehouseId) && (
                        <Typography variant="caption" color="text.secondary">
                          Stock shown is for {warehouses.find(w => w.id === selectedWarehouseId)?.name}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disabled={financialsLocked}
                      options={customersData?.items || []}
                      getOptionLabel={(option: any) => {
                        return option.business_name ||
                               option.billing_name ||
                               option.contact_person ||
                               option.email ||
                               option.mobile ||
                               'Unnamed Customer';
                      }}
                      getOptionKey={(option: any) => option.id}
                      value={selectedCustomer}
                      onChange={(_, newValue) => {
                        setSelectedCustomer(newValue);
                        setFormData(prev => ({
                          ...prev,
                          customer_id: newValue?.id || ''
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Customer"
                          size="small"
                          required
                          error={!!errors.customer_id}
                          helperText={errors.customer_id}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Invoice Date"
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Due Date"
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      disabled={financialsLocked}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        billing_address_id: '',
                        shipping_address_id: ''
                      }))}
                    >
                      Clear Addresses
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Addresses */}
          {formData.customer_id && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Billing Address (Optional)</InputLabel>
                        <Select
                          value={formData.billing_address_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, billing_address_id: e.target.value }))}
                          label="Billing Address (Optional)"
                          disabled={addressesLoading || financialsLocked}
                        >
                          <MenuItem value="">None</MenuItem>
                          {customerAddresses.filter(addr => addr.address_type === 'billing' || addr.address_type === 'both').map((address) => (
                            <MenuItem key={address.id} value={address.id}>
                              {address.address_line1}, {address.city}, {address.state} - {address.postal_code}
                              {address.is_default && ' (Default)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Shipping Address (Optional)</InputLabel>
                        <Select
                          value={formData.shipping_address_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, shipping_address_id: e.target.value }))}
                          label="Shipping Address (Optional)"
                          disabled={addressesLoading || financialsLocked}
                        >
                          <MenuItem value="">None</MenuItem>
                          {customerAddresses.filter(addr => addr.address_type === 'shipping' || addr.address_type === 'both').map((address) => (
                            <MenuItem key={address.id} value={address.id}>
                              {address.address_line1}, {address.city}, {address.state} - {address.postal_code}
                              {address.is_default && ' (Default)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Line Items */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Line Items ({formData.items.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addLineItem}
                    disabled={financialsLocked}
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Add Row
                  </Button>
                </Box>

                {errors.items && (
                  <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                    {errors.items}
                  </Alert>
                )}

                <TableContainer sx={{ maxHeight: '450px', overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '40px' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', minWidth: '200px' }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '80px' }}>MRP</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '80px' }}>
                          Stock{selectedWarehouseId && warehouses.find(w => w.id === selectedWarehouseId) ? ` (${warehouses.find(w => w.id === selectedWarehouseId)?.name.split(' ')[0]})` : ''}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Disc %</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Taxable</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Tax %</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Tax Amt</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '100px' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '50px' }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => {
                        const qty = Number(item.quantity) || 0;
                        const price = Number(item.unit_price) || 0;
                        const discountPercent = Number(item.discount_percent) || 0;

                        const lineTotal = qty * price;
                        const discountAmount = lineTotal * (discountPercent / 100);
                        const taxableAmount = lineTotal - discountAmount;
                        const taxRate = item.tax_rate ?? 18;
                        const taxAmount = taxableAmount * (taxRate / 100);
                        const finalAmount = taxableAmount + taxAmount;

                        const selectedVariant = productsData?.items?.flatMap((p: any) => 
                          (p.variants || []).map((v: any) => ({
                            ...v,
                            product_name: p.name,
                            product_id: p.id,
                            is_set_selling: p.is_set_selling || false,
                            set_size: p.set_size || 1,
                            moq: p.moq || 1,
                            product_unit: p.product_unit || 'pcs',
                          }))
                        ).find((v: any) => v.id === item.variant_id);

                        return (
                          <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {index + 1}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Autocomplete
                                disabled={financialsLocked}
                                options={productsData?.items?.flatMap((p: any) =>
                                  (p.variants || []).map((v: any) => ({
                                    ...v,
                                    product_name: p.name,
                                    product_id: p.id,
                                    hsn_code: p.hsn_code,
                                    is_set_selling: p.is_set_selling || false,
                                    set_size: p.set_size || 1,
                                    moq: p.moq || 1,
                                    product_unit: p.product_unit || 'pcs',
                                  }))
                                ) || []}
                                getOptionLabel={(option: any) => `${option.product_name || 'Unknown'} - ${option.name}`}
                                getOptionKey={(option: any) => option.id}
                                value={selectedVariant || null}
                                onChange={(_, newValue) => {
                                  if (newValue) {
                                    const basePrice = Number(newValue.sale_price || newValue.mrp || 0);
                                    const sz = newValue.set_size || 1;
                                    const unitPrice = newValue.is_set_selling ? basePrice * sz : basePrice;
                                    const initialQty = newValue.moq || 1;
                                    
                                    // Fetch stock info for the selected warehouse
                                    fetchStockInfo(newValue.id, selectedWarehouseId);
                                    
                                    setFormData(prev => ({
                                      ...prev,
                                      items: prev.items.map((item, i) => 
                                        i === index ? {
                                          ...item,
                                          variant_id: newValue.id,
                                          unit_price: unitPrice,
                                          quantity: initialQty,
                                          hsn_code: newValue.hsn_code || ''
                                        } : item
                                      )
                                    }));
                                  } else {
                                    updateLineItem(index, 'variant_id', '');
                                    updateLineItem(index, 'unit_price', 0);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select product"
                                    error={!!errors[`item_${index}_variant`]}
                                    sx={{ 
                                      minWidth: 200,
                                      '& .MuiInputBase-root': { fontSize: '0.8125rem', py: 0.25 },
                                      '& .MuiInputBase-input': { py: '4px !important' }
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {selectedVariant?.mrp ? `₹${Number(selectedVariant.mrp).toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {selectedVariant ? (
                                <Tooltip title={`Total: ${stockInfo[item.variant_id]?.current_stock ?? 0} | Reserved: ${stockInfo[item.variant_id]?.reserved_stock ?? 0}`}>
                                  <span>{stockInfo[item.variant_id]?.available_stock ?? selectedVariant.total_available ?? 0}</span>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={item.quantity}
                                disabled={financialsLocked}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d+$/.test(val)) {
                                    updateLineItem(index, 'quantity', val === '' ? '' : parseInt(val));
                                  }
                                }}
                                sx={{
                                  width: 65,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                                error={!!errors[`item_${index}_quantity`]}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={item.unit_price}
                                disabled={financialsLocked}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                    updateLineItem(index, 'unit_price', val);
                                  }
                                }}
                                sx={{
                                  width: 85,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                                error={!!errors[`item_${index}_price`]}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                size="small"
                                value={item.discount_percent}
                                disabled={financialsLocked}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                    updateLineItem(index, 'discount_percent', val);
                                  }
                                }}
                                sx={{
                                  width: 65,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', fontWeight: 500 }}>
                              ₹{taxableAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {taxRate}%
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', color: 'info.main' }}>
                              ₹{taxAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', fontWeight: 600, color: 'primary.main' }}>
                              ₹{finalAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeLineItem(index)}
                                disabled={formData.items.length === 1 || financialsLocked}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Charges and Totals Summary */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={2}>
                  {/* Left Column: Charges */}
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Charges and Discounts
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Discount Amount (₹)"
                          value={formData.discount_amount}
                          disabled={financialsLocked}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                              setFormData(prev => ({ ...prev, discount_amount: val === '' ? '' : parseFloat(val) }));
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Shipping Charges (₹)"
                          value={formData.shipping_charges}
                          disabled={financialsLocked}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                              setFormData(prev => ({ ...prev, shipping_charges: val === '' ? '' : parseFloat(val) }));
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Packing Charges (₹)"
                          value={formData.packing_charges}
                          disabled={financialsLocked}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                              setFormData(prev => ({ ...prev, packing_charges: val === '' ? '' : parseFloat(val) }));
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Right Column: Calculation Preview */}
                  <Grid item xs={12} md={5}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2">Items Subtotal:</Typography>
                          <Typography variant="body2">{formatCurrency(itemsSubtotal)}</Typography>
                        </Box>
                        {Number(formData.discount_amount) > 0 && (
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Total Discount:</Typography>
                            <Typography variant="body2" color="success.main">
                              -{formatCurrency(Number(formData.discount_amount) || 0)}
                            </Typography>
                          </Box>
                        )}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2">Shipping Charges:</Typography>
                          <Typography variant="body2">{formatCurrency(Number(formData.shipping_charges) || 0)}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2">Packing Charges:</Typography>
                          <Typography variant="body2">{formatCurrency(Number(formData.packing_charges) || 0)}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2">Taxable Amount:</Typography>
                          <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2">GST (18%):</Typography>
                          <Typography variant="body2">{formatCurrency(taxAmount)}</Typography>
                        </Box>
                        <Divider sx={{ my: 0.75 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="subtitle1" fontWeight={600} color="primary">
                            Total Amount:
                          </Typography>
                          <Typography variant="subtitle1" fontWeight={600} color="primary">
                            {formatCurrency(total)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <TextField
                  fullWidth
                  label="Invoice Notes"
                  multiline
                  rows={3}
                  size="small"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default InvoiceForm;